import { FC } from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Grid2, Collapse, IconButton, useTheme } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { RouteToSettings } from "@routing/RouteMap";
import { icon } from "@/app/style/icons";
import { useQuery } from "@apollo/client";
import { Query } from "@/app/graphql/codegen";
import { FETCH_COMPANY } from "@company/graphql/queries/fetchCompany";
import { alpha } from "@mui/material/styles";
import { useIsAdmin } from "@auth/hooks/useIsAdmin";

// Types for our navigation structure
interface NavItem {
    path: string;
    label: string;
    icon?: React.ElementType;
    children?: NavItem[];
    adminOnly?: boolean;
}

interface NavSection {
    title?: string;
    items: NavItem[];
}

// Recursive component for rendering menu items
const NavMenuItem: FC<{
    item: NavItem;
    level?: number;
    currentPath: string;
    onItemClick: (path: string) => void;
}> = ({ item, level = 0, currentPath, onItemClick }) => {
    const theme = useTheme();
    const isSelected = currentPath.startsWith(item.path);
    const hasChildren = item.children && item.children.length > 0;

    return (
        <>
            <ListItemButton
                onClick={() => onItemClick(item.path)}
                selected={isSelected}
                sx={{
                    pl: 2 + level * 2,
                    py: 1,
                    bgcolor: isSelected ? `${alpha(theme.palette.semantic.button.primary, 0.25)} !important` : "transparent",
                    "&:hover": {
                        bgcolor: isSelected ? theme.palette.semantic.button.primary : "rgba(0, 0, 0, 0.04)",
                    },
                    borderRadius: 1,
                }}
            >
                {item.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <item.icon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                )}
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        variant: "body2",
                        sx: {
                            fontWeight: isSelected ? 500 : 400,
                        },
                    }}
                />
            </ListItemButton>

            {hasChildren && (
                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.children?.map((child, index) => <NavMenuItem key={child.path} item={child} level={level + 1} currentPath={currentPath} onItemClick={onItemClick} />)}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const SettingsLayout: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAdmin } = useIsAdmin();

    const { data } = useQuery<Pick<Query, "company">>(FETCH_COMPANY);

    // Navigation structure
    const navigationSections: NavSection[] = [
        {
            title: "Mein Profil",
            items: [
                {
                    path: RouteToSettings.PROFILE_GENERAL,
                    label: "Profileinstellungen",
                    icon: icon.settings,
                },
                // Dashboard page temporarily hidden – uncomment when content ready
                // {
                //     path: RouteToSettings.DASHBOARD,
                //     label: "Dashboard",
                //     icon: icon.dashboard,
                // },
                {
                    path: RouteToSettings.CHAT,
                    label: "Chateinstellungen",
                    icon: icon.chat,
                },
                /*
                {
                    path: RouteToSettings.PROFILE_NEWSLETTER,
                    label: "Newsletter",
                    icon: icon.newsletter,
                },
                */
            ],
        },
        {
            title: "Meine Firma",
            items: [
                {
                    path: RouteToSettings.COMPANY_SUBSCRIPTION,
                    label: "Abonnement",
                    icon: icon.premium,
                    adminOnly: true,
                },
                {
                    path: isAdmin ? RouteToSettings.COMPANY_PROFILE : RouteToSettings.COMPANY_PROFILE_READONLY,
                    label: "Firmenprofil",
                    icon: icon.company,
                },
                {
                    path: RouteToSettings.COMPANY_USERS,
                    label: "Nutzerverwaltung",
                    icon: icon.users,
                    adminOnly: true,
                },
                {
                    path: RouteToSettings.LAW_SELECTION,
                    label: "Gesetze",
                    icon: icon.law,
                },
            ],
        },
        {
            title: "Shop",
            items: [
                {
                    path: RouteToSettings.SHOP_SAAS,
                    label: "ESGbot SaaS",
                    icon: icon.shop,
                    adminOnly: true,
                },
                {
                    path: RouteToSettings.SHOP_EXTRA_PAGES,
                    label: "Extraseiten",
                    icon: icon.shop,
                    adminOnly: true,
                },
                {
                    path: RouteToSettings.SHOP_AI_SERVICE,
                    label: "KI-Services",
                    icon: icon.shop,
                    adminOnly: true,
                },
                {
                    path: RouteToSettings.SHOP_CONSULTING,
                    label: "Consulting",
                    icon: icon.shop,
                    adminOnly: true,
                },
            ],
        },
    ];

    // Filter items based on admin status
    const filteredSections = navigationSections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => !item.adminOnly || isAdmin),
        }))
        .filter((section) => section.items.length > 0);

    const handleBackButtonClick = () => {
        const lastNonSettingsPath = localStorage.getItem("lastNonSettingsPath");
        const defaultPath = "/dashboard"; // Default path if no previous page is found

        console.log("Settings back button clicked:", { lastNonSettingsPath });

        if (lastNonSettingsPath) {
            navigate(lastNonSettingsPath);
        } else {
            navigate(defaultPath);
        }
    };

    return (
        <>
            {/* Full-height row. Prevent wrapping & hide overflow so the outer AppLayout never scrolls. */}
            <Grid2 container spacing={2} sx={{ height: "100%", flexWrap: "nowrap", overflow: "hidden" }}>
                {/* Sidebar – fixed, never scrolls with settings content */}
                <Grid2 size={3} sx={{ flexShrink: 0 }}>
                    <Box sx={{ pr: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                            <IconButton onClick={handleBackButtonClick} sx={{ mr: 1, ml: -1 }}>
                                <icon.back />
                            </IconButton>

                            <Typography variant="h6">Profileinstellungen</Typography>
                        </Box>

                        {filteredSections.map((section, index) => (
                            <Box key={index} sx={{ mb: 3 }}>
                                {section.title && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                            display: "block",
                                            mb: 1,
                                            ml: 2,
                                            textTransform: "uppercase",
                                            fontSize: "0.8rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                )}
                                <List
                                    component="nav"
                                    sx={{
                                        "& .MuiListItemButton-root": {
                                            mb: 0.5,
                                        },
                                    }}
                                >
                                    {section.items.map((item) => (
                                        <NavMenuItem key={item.path} item={item} currentPath={location.pathname} onItemClick={navigate} />
                                    ))}
                                </List>
                            </Box>
                        ))}
                    </Box>
                </Grid2>

                {/* Main content – becomes scrollable if taller than the viewport */}
                <Grid2 size={9} sx={{ overflowY: "auto", maxHeight: "100%" }}>
                    <Box
                        sx={{
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            pl: 3,
                            pr: 1,
                            minHeight: "100%",
                        }}
                    >
                        <Outlet />
                    </Box>
                </Grid2>
            </Grid2>
        </>
    );
};

export default SettingsLayout;
